import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { api } from '../../api';
import { useInventory, UNITS } from '../../contexts/InventoryContext';
import useDetectedDocuments from '../../hooks/useDetectedDocuments';
import { announceSegmentsChanged, rulingLabel } from '../../hooks/useDocumentSegments';
import { useThumbnailUrl } from '../../hooks/useThumbnailUrl';
import { usePageThumbnailUrl } from '../../hooks/usePageThumbnailUrl';
import useWordFit from '../../hooks/useWordFit';
import {
  getCanonicalType, getTypeIcon, getDocTypeDisplay, getDocCategory, getSummary, cleanSummary, formatSize, uploadedAt,
} from '../../hooks/useInventoryData';
import { scanBadge } from '../../constants/scanStatus';
import AudioCard from './AudioCard';
import DocumentDetailPanel from './DocumentDetailPanel';
import SegMenu from '../segments/SegMenu';
import SegInlineEdit from '../segments/SegInlineEdit';
import UndoToast, { useUndoToast } from '../segments/UndoToast';
import { fmtType } from '../segments/segmentTypes';
import { isCutChild, pagesRange } from '../segments/cutChild';
import { docTitle } from '../../domain/documentTitle';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';

// ── Grid — the inventory's card view, one renderer for both units (plan §3.4c) ──
// The UNIT decides what a card is: in Files, every uploaded file (a cut file wears
// a "13 documents inside" badge); in Documents, every document — the ones cut out
// of a file are cards of their own and the container steps back unless "Show
// source files" is on. Rows arrive already scoped (useInventoryData.scopeToUnit).
//
// Every card reads the same way, in four slots of fixed order and weight (plan
// §3.4d): TITLE (two lines, cut only between words) · ORIGIN, one cue-led line —
// "⤴ from <file> · pp. a–b" for a document cut out of a file, "📁 <folder> · <date>"
// for an uploaded file; it wraps, it is never truncated · SUMMARY (three lines,
// the whole text and the names on hover) · FOOTER chips (type · search readiness
// or status). Pages, "13 documents inside" and Reading… / Updating… sit on the
// image. Rows density lays the same four slots out as aligned columns.
//
// A click SELECTS the card and opens the same detail pane the list expands
// (summary, entities, readiness, classification, actions; "Documents in this
// file" for a cut file); double-click or Enter opens the viewer. List and Grid
// are two presentations of one record.
//
// Windowed (plan §3.4c step C): the Grid mounts only the lines near the viewport — a
// line is a group header or one row of cards — so a case of thousands opens as fast as
// one of fifty. The page (window) scrolls; lines are measured as they mount. The root
// carries the whole picture as data attributes (data-cards*, data-groups) for anything
// that needs counts without the cards being in the DOM.
//
// Automatic first (plan §7d): a detected document is simply a document — nothing to
// approve. Corrections live in the hover "⋯" menu (Merge with previous · Split… ·
// Rename · Change type), every one answers with an Undo toast. The only marker is
// "?" on a boundary the system itself was unsure about.
//
// Two providers feed the cards. Virtual items: the case-level /detected-documents
// read, for files that were detected but not yet cut (their parent is still a plain
// file; clicking such a card opens the parent's details). Real children: rows cut
// out of a compilation (container_id + page_start/page_end), which arrive in the
// inventory list like any document. Until a re-cut lands, rows about to be
// replaced read "Updating…".

// Canonical legal order for the Type grouping — the backend PurposeGroup
// labels (domain/document_purpose.py), plus "Other" for what has no group yet.
const TYPE_GROUP_ORDER = [
  'Pleadings & Filings',
  'Investigation & Reports',
  'Statements & Testimony',
  'Evidence & Forensics',
  'Correspondence',
  'Records — Medical',
  'Records — Financial',
  'Records — Government & Third-Party',
  'Contracts & Agreements',
  'Media',
  'Case Administration',
  'Other',
];
const OTHER_GROUP = 'Other';

// The detector's doc_type vocabulary → PurposeGroup. Unknown types fall to Other.
const DOC_TYPE_GROUP = {
  police_report: 'Investigation & Reports', incident_report: 'Investigation & Reports',
  supplemental_report: 'Investigation & Reports', supplement: 'Investigation & Reports',
  arrest_report: 'Investigation & Reports', detective_report: 'Investigation & Reports',
  k9_report: 'Investigation & Reports', report: 'Investigation & Reports', narrative: 'Investigation & Reports',
  cad_log: 'Investigation & Reports', dispatch_log: 'Investigation & Reports', use_of_force_report: 'Investigation & Reports',
  witness_statement: 'Statements & Testimony', victim_statement: 'Statements & Testimony',
  defendant_statement: 'Statements & Testimony', statement: 'Statements & Testimony',
  interview: 'Statements & Testimony', interview_transcript: 'Statements & Testimony',
  transcript: 'Statements & Testimony', deposition: 'Statements & Testimony',
  lab_report: 'Evidence & Forensics', forensic_report: 'Evidence & Forensics', dna_report: 'Evidence & Forensics',
  toxicology_report: 'Evidence & Forensics', autopsy_report: 'Evidence & Forensics', evidence_log: 'Evidence & Forensics',
  chain_of_custody: 'Evidence & Forensics', property_receipt: 'Evidence & Forensics', photo_log: 'Evidence & Forensics',
  fingerprint_report: 'Evidence & Forensics', firearms_report: 'Evidence & Forensics',
  warrant: 'Pleadings & Filings', search_warrant: 'Pleadings & Filings', arrest_warrant: 'Pleadings & Filings',
  affidavit: 'Pleadings & Filings', charging_document: 'Pleadings & Filings', information: 'Pleadings & Filings',
  indictment: 'Pleadings & Filings', complaint: 'Pleadings & Filings', motion: 'Pleadings & Filings',
  order: 'Pleadings & Filings', court_order: 'Pleadings & Filings', subpoena: 'Pleadings & Filings',
  letter: 'Correspondence', email: 'Correspondence', memo: 'Correspondence', correspondence: 'Correspondence', fax: 'Correspondence',
  medical_record: 'Records — Medical', ems_report: 'Records — Medical', hospital_record: 'Records — Medical',
  bank_statement: 'Records — Financial', financial_record: 'Records — Financial', invoice: 'Records — Financial',
  phone_records: 'Records — Government & Third-Party', cell_records: 'Records — Government & Third-Party',
  dmv_record: 'Records — Government & Third-Party', criminal_history: 'Records — Government & Third-Party',
  photo: 'Media', photograph: 'Media', video: 'Media', audio: 'Media',
  probable_cause_statement: 'Pleadings & Filings',
  suspect_statement: 'Statements & Testimony', interview_summary: 'Statements & Testimony',
  cad_report: 'Investigation & Reports', photo_album: 'Media',
  cover_sheet: OTHER_GROUP, form: OTHER_GROUP, other: OTHER_GROUP, unknown: OTHER_GROUP,
};

// Windowing geometry — mirrors app.css: .inv-gal-grid columns are minmax(190px, 1fr)
// with a 12px gap inside 6px side padding; the estimates are replaced by measurements.
const CARD_MIN = 190;
const CARD_GAP = 12;
const GRID_PAD_X = 12;
const LINE_EST = { head: 43, cards: 462, rows: 104 };
const OVERSCAN = { cards: 3, rows: 8 };

// How long after a correction the gallery trusts "a re-cut is coming" without seeing the job.
const RECENT_RULING_MS = 45_000;

// Group-by keys; "Source file" only means something in the Documents unit.
const GROUP_BY = [
  { key: 'type', label: 'Type' },
  { key: 'file', label: 'Source file', units: [UNITS.DOCUMENTS] },
  { key: 'folder', label: 'Folder' },
  { key: 'date', label: 'Uploaded' },
];

// Pipeline status shown in a card's footer (a scan verdict outranks it).
const STATUS_BADGES = {
  processing: { label: 'Processing', cls: 'inv-status--processing' },
  pending:    { label: 'Pending',    cls: 'inv-status--queued' },
  queued:     { label: 'Queued',     cls: 'inv-status--queued' },
  failed:     { label: 'Failed',     cls: 'inv-status--error' },
  error:      { label: 'Error',      cls: 'inv-status--error' },
};

const naturalCompare = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
const monthLabel = (iso) => {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Unknown date' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
};
const clip = (text, n) => {
  const t = String(text || '').trim();
  if (t.length <= n) return t;
  const cut = t.slice(0, n);
  const sp = cut.lastIndexOf(' ');
  return `${sp > n * 0.6 ? cut.slice(0, sp) : cut}…`;
};
const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
// "/Evidence and Forensics/Forensic Report" → "Evidence and Forensics › Forensic Report"
const folderLabel = (path) => String(path || '').split('/').filter(Boolean).join(' › ') || 'Unfiled';
// The summary slot holds three lines; the DOM text is capped well past that (rows density shows more).
const SUMMARY_CHARS = 360;

// Click vs double-click on a card. A single click SELECTS (opens the detail pane, which
// narrows the grid and reflows the cards); a double-click OPENS the viewer. The select is
// held for the double-click window so the second click lands on the same, unmoved card.
const DBLCLICK_MS = 220;
function useClickOrDouble(onSingle, onDouble) {
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const onClick = useCallback((e) => {
    if (e.detail > 1) return;                     // part of a double-click: the dblclick handler owns it
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; onSingle(); }, DBLCLICK_MS);
  }, [onSingle]);
  const onDoubleClick = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    onDouble();
  }, [onDouble]);
  return { onClick, onDoubleClick };
}

// Deferred work until the card is on (or near) the screen.
function useInView(rootMargin = '300px') {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [inView, rootMargin]);
  return [ref, inView];
}

// ── the gallery's unit ────────────────────────────────────────────────────
// {kind:'file', doc, file?, parent?}   — a file, a cut file (container), or a document cut out of one
// {kind:'child', doc(parent), item}    — a document detected inside `doc` that is not cut out yet
const SINGLE_FILES_GROUP = 'Files with one document';

function groupKeyFor(entry, groupBy) {
  const { doc } = entry;
  switch (groupBy) {
    // Source-file grouping: only files that hold several documents get their own header;
    // every plain file collapses into one trailing group (395 one-card groups is noise).
    case 'file':
      if (entry.parent) return entry.parent.filename || 'Untitled file';      // a real child sits under its source file
      return (entry.kind === 'child' || entry.file?.is_compilation) ? (doc.filename || 'Untitled file') : SINGLE_FILES_GROUP;
    case 'folder': return doc.folder_path || 'Unfiled';
    case 'date': return monthLabel(uploadedAt(doc));
    case 'type':
    default: {
      if (entry.kind === 'child') {
        const t = String(entry.item.doc_type || '').toLowerCase();
        return DOC_TYPE_GROUP[t] || OTHER_GROUP;
      }
      const g = getDocCategory(doc);
      return g && g !== 'Unclassified' ? g : OTHER_GROUP;
    }
  }
}

function orderGroups(keys, groupBy) {
  if (groupBy === 'type') {
    const idx = (k) => { const i = TYPE_GROUP_ORDER.indexOf(k); return i === -1 ? TYPE_GROUP_ORDER.length : i; };
    return keys.sort((a, b) => idx(a) - idx(b) || naturalCompare(a, b));
  }
  if (groupBy === 'date') return keys.sort((a, b) => (new Date(b) - new Date(a)) || naturalCompare(a, b));
  if (groupBy === 'file') {
    return keys.sort((a, b) => (a === SINGLE_FILES_GROUP) - (b === SINGLE_FILES_GROUP) || naturalCompare(a, b));
  }
  return keys.sort(naturalCompare);
}

// Reading order inside a group: source file (natural), then page.
function readingOrder(a, b) {
  const f = naturalCompare(a.parent?.filename || a.doc.filename, b.parent?.filename || b.doc.filename);
  if (f) return f;
  const pa = a.kind === 'child' ? a.item.page_start : (a.doc.page_start || 0);
  const pb = b.kind === 'child' ? b.item.page_start : (b.doc.page_start || 0);
  return pa - pb;
}

// ── the card's slots (plan §3.4d) ─────────────────────────────────────────
// ORIGIN — where the document comes from, led by one cue per kind.
function OriginFrom({ file, start, end, onOpen }) {
  return (
    <button
      type="button"
      className="inv-gal-origin inv-gal-origin--from"
      data-testid="gallery-origin" data-origin="from"
      title="Open the source file in the viewer at this document's first page"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <span className="inv-gal-origin-cue">⤴ from</span> {file || 'its source file'} · <span className="inv-gal-nowrap">{pagesRange(start, end)}</span>
    </button>
  );
}

function OriginFolder({ doc }) {
  const date = shortDate(uploadedAt(doc));
  return (
    <div className="inv-gal-origin inv-gal-origin--folder" data-testid="gallery-origin" data-origin="folder" title={`${doc.folder_path || 'Unfiled'}${date ? ` · uploaded ${date}` : ''}`}>
      <span className="inv-gal-origin-cue" aria-hidden="true">📁</span> {folderLabel(doc.folder_path)}{date && <> · <span className="inv-gal-nowrap">{date}</span></>}
    </div>
  );
}

// SUMMARY — what the document says; the slot keeps its height when there is none yet.
function Summary({ doc }) {
  const card = doc.extraction?.document_card;
  // the document-level summary says what the document IS; the refined analysis summary
  // (getSummary's first choice) opens with how the file was read ("This 3-page PDF…")
  const full = cleanSummary(doc.summary || doc.analysis?.summary_synthesis) || getSummary(doc) || card?.summary || '';
  const names = Array.isArray(card?.entities) ? card.entities.filter(Boolean) : [];
  const hover = [full, names.length ? `Names: ${names.join(' · ')}` : ''].filter(Boolean).join('\n\n');
  return (
    <div className={`inv-gal-gist${full ? '' : ' inv-gal-gist--empty'}`} data-testid="gallery-gist" title={hover || undefined}>
      {clip(full, SUMMARY_CHARS)}
    </div>
  );
}

// FOOTER — type · search readiness (or the status that stands in for it); nothing else.
function Foot({ doc, type, dupCount = 0, onDupFilter }) {
  const scan = scanBadge(doc);
  const badge = scan
    ? { label: scan.label, cls: scan.invCls, title: scan.title }
    : (STATUS_BADGES[String(doc.status || '').toLowerCase()] || null);
  const tier = !badge && (doc.tier === 'deep' || doc.tier === 'surface') ? doc.tier : null;
  return (
    <div className="inv-gal-foot" data-testid="gallery-foot">
      {type ? <span className="inv-gal-type" title={type}>{type}</span> : <span className="inv-gal-type inv-gal-type--none">unlabelled</span>}
      {badge && <span className={`inv-status inv-status--sm ${badge.cls}`} title={badge.title || ''}>{badge.label}</span>}
      {tier && <span className={`inv-tier-badge inv-tier--${tier} inv-tier-badge--sm`} title="Search readiness">✓ {tier === 'deep' ? 'Deep' : 'Surface'} search</span>}
      {doc.derived_kind === 'excerpt' && (
        <span className="inv-tier-badge inv-derived-badge inv-derived--excerpt inv-tier-badge--sm" title="Cut from a longer recording — open it to see where">✂ Excerpt</span>
      )}
      {doc.derived_kind === 'redaction' && (
        <span className="inv-tier-badge inv-derived-badge inv-derived--redaction inv-tier-badge--sm" title="Redacted copy — the original is untouched">🖍️ Redacted copy</span>
      )}
      {dupCount > 1 && (
        <span
          className="inv-gal-dup" title={`${dupCount} copies of this file`}
          onClick={(e) => { e.stopPropagation(); onDupFilter(doc.file_hash_sha256); }}
        >
          ×{dupCount}
        </span>
      )}
    </div>
  );
}

// ── cards ────────────────────────────────────────────────────────────────
// A document detected inside a file that is not cut out yet (no row of its own).
function ChildCard({ entry, fitTitle, onSelect, onOpen, onRule, busy }) {
  const { doc, item } = entry;
  const [ref, inView] = useInView();
  const [editing, setEditing] = useState(null);   // 'rename' | 'retype' | null
  const { src } = usePageThumbnailUrl(item.parent_id, item.page_start, inView);
  const title = item.title || fmtType(item.doc_type) || `Document ${item.index}`;
  const type = fmtType(item.doc_type);
  const clicks = useClickOrDouble(
    useCallback(() => onSelect(item.parent_id), [onSelect, item.parent_id]),
    useCallback(() => onOpen(item.parent_id, item.page_start), [onOpen, item.parent_id, item.page_start]),
  );
  const menu = [
    item.page_start > 1 && { key: 'merge', label: '⤒ Merge with previous', title: 'Not a separate document — these pages belong to the one above', danger: true,
      onSelect: () => onRule(item.parent_id, 'dismiss', item.page_start, {}, item) },
    { key: 'split', label: '✂ Split…', title: 'Open the file here and mark where another document starts',
      onSelect: () => onOpen(item.parent_id, item.page_start) },
    { key: 'rename', label: '✎ Rename', onSelect: () => setEditing('rename') },
    { key: 'retype', label: '⌥ Change type', onSelect: () => setEditing('retype') },
  ];
  return (
    <article
      ref={ref}
      className="inv-gal-card inv-gal-card--child seg-hover-host"
      data-gal-kind="child" data-gal-parent={item.parent_id} data-gal-page={item.page_start}
      tabIndex={0}
      onClick={clicks.onClick}
      onDoubleClick={clicks.onDoubleClick}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(item.parent_id, item.page_start); } else if (e.key === ' ') { e.preventDefault(); onSelect(item.parent_id); } }}
      title={item.anchor ? `First line: ${item.anchor}` : undefined}
    >
      <div className="inv-gal-thumb">
        {src ? <img src={src} alt="" loading="lazy" /> : <div className="inv-gal-thumb-ph">📄</div>}
        <span className="inv-gal-pages">{item.page_count} pp</span>
        {item.unsure && <span className="seg-unsure" title="The detector was not sure a document starts here. Open it, or merge it if it is a continuation.">?</span>}
      </div>
      {onRule && <SegMenu items={menu} />}
      <div className="inv-gal-body">
        <div className="inv-gal-title" data-full-title={title} title={title}>{fitTitle(title)}</div>
        <OriginFrom file={doc.filename} start={item.page_start} end={item.page_end} onOpen={() => onOpen(item.parent_id, item.page_start)} />
        <div className="inv-gal-gist inv-gal-gist--pending" data-testid="gallery-gist">Being cut out of its file — its own summary follows.</div>
        <div className="inv-gal-foot" data-testid="gallery-foot">
          {type ? <span className="inv-gal-type" title={type}>{type}</span> : <span className="inv-gal-type inv-gal-type--none">unlabelled</span>}
        </div>
        {editing && (
          <SegInlineEdit
            kind={editing} busy={busy}
            value={editing === 'rename' ? (item.title || '') : (item.doc_type || '')}
            onCancel={() => setEditing(null)}
            onSave={(extra) => { setEditing(null); onRule(item.parent_id, editing, item.page_start, extra, item); }}
          />
        )}
      </div>
    </article>
  );
}

// A row of the inventory: a file, a cut file (container), or a document cut out of one.
function FileCard({ entry, fitTitle, selected, checked = false, onSelect, onToggleCheck, onDupFilter, onOpen, onRule, busy, updating = false, dupCount = 0 }) {
  const { doc, file, parent } = entry;
  const isRealChild = isCutChild(doc);                                // materialized out of a compilation (Phase 2)
  const [ref, inView] = useInView();
  const [editing, setEditing] = useState(false);                      // rename (real children only)
  const reading = doc.status === 'pending' || doc.status === 'processing';
  const ownTitle = docTitle(doc, parent?.filename);
  const clicks = useClickOrDouble(
    useCallback(() => onSelect(doc.id), [onSelect, doc.id]),
    useCallback(() => onOpen(doc.id), [onOpen, doc.id]),
  );
  // corrections on a real child go to its parent file's ledger (its type is its own classification: the row's editor)
  const menu = isRealChild && onRule && !updating ? [
    doc.page_start > 1 && { key: 'merge', label: '⤒ Merge with previous', title: 'Not a separate document — these pages belong to the one above', danger: true,
      onSelect: () => onRule(doc.container_id, 'dismiss', doc.page_start, {}, { title: ownTitle }) },
    { key: 'split', label: '✂ Split…', title: 'Open the source file here and mark where another document starts',
      onSelect: () => onOpen(doc.container_id, doc.page_start) },
    { key: 'rename', label: '✎ Rename', onSelect: () => setEditing(true) },
  ] : null;
  // viewport-lazy: a case is hundreds of cards — fetching every thumbnail on mount floods the
  // connection pool and starves whatever the attorney clicks next (the viewer's own request)
  const { src: ownThumb } = useThumbnailUrl(doc.id, !!doc.has_thumbnail && inView);
  // a child that inherited the parent's text skipped extraction, where thumbnails are made:
  // until its own exists, show the parent's page (the same pixels)
  const { src: pageThumb } = usePageThumbnailUrl(isRealChild && !doc.has_thumbnail ? doc.container_id : null, doc.page_start, inView);
  const src = ownThumb || pageThumb;
  const ct = getCanonicalType(doc);
  const icon = getTypeIcon(ct, doc.document_type);
  const type = getDocTypeDisplay(doc);
  const pageCount = isRealChild ? doc.page_end - doc.page_start + 1 : (doc.extraction?.page_count || doc.page_count || 0);
  const audioTile = !src && ct === 'audio' && !!doc.extraction?.audio_card;     // carries its own length
  const isContainer = !!file?.is_compilation;
  const inside = isContainer ? (file.segment_count || 0) : 0;
  return (
    <article
      ref={ref}
      className={`inv-gal-card inv-gal-card--file${isContainer ? ' inv-gal-card--container' : ''}${isRealChild ? ' seg-hover-host' : ''}${updating ? ' inv-gal-card--updating' : ''}${selected ? ' inv-gal-card--selected' : ''}${checked ? ' inv-gal-card--checked' : ''}`}
      data-gal-kind={isContainer ? 'container' : (isRealChild ? 'realchild' : 'file')} data-gal-doc={doc.id}
      data-gal-selected={selected ? '1' : undefined}
      data-gal-updating={updating ? '1' : undefined}
      data-gal-parent={isRealChild ? doc.container_id : undefined} data-gal-page={isRealChild ? doc.page_start : undefined}
      tabIndex={0}
      aria-selected={selected}
      onClick={clicks.onClick}
      onDoubleClick={clicks.onDoubleClick}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(doc.id); } else if (e.key === ' ') { e.preventDefault(); onSelect(doc.id); } }}
    >
      <label className="inv-gal-check" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} title="Select for a bulk action">
        <input type="checkbox" checked={checked} onChange={() => onToggleCheck(doc.id)} />
      </label>
      <div className="inv-gal-thumb">
        {src
          ? <img src={src} alt="" loading="lazy" style={doc.display_rotation ? { transform: `rotate(${doc.display_rotation}deg)`, transformOrigin: 'center center' } : undefined} />
          : (audioTile ? <AudioCard doc={doc} /> : <div className="inv-gal-thumb-ph">{icon}</div>)}
        {pageCount > 0
          ? <span className="inv-gal-pages">{pageCount} pp</span>
          : (!audioTile && doc.file_size ? <span className="inv-gal-pages">{formatSize(doc.file_size)}</span> : null)}
        {isContainer && <span className="inv-gal-state inv-gal-state--container" title="This file holds several documents; switch to Documents to see them as their own cards">{inside} {inside === 1 ? 'document' : 'documents'} inside</span>}
        {updating
          ? <span className="inv-gal-state inv-gal-state--busy" title="A correction changed this document; it is being cut again from the source file">Updating…</span>
          : (reading && <span className="inv-gal-state inv-gal-state--busy" title="This document is still being read; its summary and type appear when that is done">Reading…</span>)}
      </div>
      {menu && <SegMenu items={menu} />}
      <div className="inv-gal-body">
        <div className="inv-gal-title" data-full-title={ownTitle} title={doc.filename}>{fitTitle(ownTitle)}</div>
        {isRealChild
          ? <OriginFrom file={parent?.filename} start={doc.page_start} end={doc.page_end} onOpen={() => onOpen(doc.container_id, doc.page_start)} />
          : <OriginFolder doc={doc} />}
        <Summary doc={doc} />
        <Foot doc={doc} type={type} dupCount={dupCount} onDupFilter={onDupFilter} />
        {editing && (
          <SegInlineEdit
            kind="rename" busy={busy} value={ownTitle}
            onCancel={() => setEditing(false)}
            onSave={(extra) => { setEditing(false); onRule(doc.container_id, 'rename', doc.page_start, extra, { title: ownTitle }); }}
          />
        )}
      </div>
    </article>
  );
}

const MemoChild = memo(ChildCard);
const MemoFile = memo(FileCard);

// ── the view ─────────────────────────────────────────────────────────────
export default function DocumentGallery({
  caseId, unit = UNITS.DOCUMENTS, docs, allDocs = null, folderPaths = [], hashCountMap = {},
  onDocChange, onShowDetail, onShowViewer,
}) {
  // read the context ONCE here and hand cards plain props, so a keystroke in the search box
  // does not re-render every one of several hundred memoized cards
  const { dispatch, showSourceFiles, selected: checkedSet } = useInventory();
  const [groupBy, setGroupBy] = useState('type');
  const [density, setDensity] = useState('cards');
  const [detailId, setDetailId] = useState(null);           // the card whose details are open
  const [busy, setBusy] = useState(false);
  const [ruleError, setRuleError] = useState(null);
  const { toast, show: showToast, clear: clearToast } = useUndoToast();
  const undoRef = useRef(null);                     // parent_id the toast's Undo applies to
  const { data, loading, error, refresh, filesById, itemsByParent } = useDetectedDocuments(caseId);
  const { jobs } = useBackgroundJobs();
  const [ruledAt, setRuledAt] = useState(() => new Map());   // parent id → when a correction was last made here
  const isDocsUnit = unit === UNITS.DOCUMENTS;
  // titles end on a whole word: every card's title box is the same width, measured once per layout
  const galleryRef = useRef(null);
  const fitTitle = useWordFit(galleryRef, '.inv-gal-title', 2, density);

  // group-by keys that mean something in this unit
  const groupOptions = useMemo(() => GROUP_BY.filter((g) => !g.units || g.units.includes(unit)), [unit]);
  useEffect(() => { if (!groupOptions.some((g) => g.key === groupBy)) setGroupBy('type'); }, [groupOptions, groupBy]);

  // "Updating…" is shown only while a re-cut is really on its way: the file's child rows
  // disagree with its ledger (cut.state) AND the job is queued/running, or the correction
  // was made moments ago (the job poll has not seen it yet). Never a standing label.
  const updatingRanges = useMemo(() => {
    const active = new Set();
    for (const j of jobs || []) {
      const st = String(j.status || '').toLowerCase();
      if (j.job_type === 'materialize_segments' && (st === 'queued' || st === 'processing') && j.params?.document_id) active.add(j.params.document_id);
    }
    const now = Date.now();
    const out = new Map();                                     // parent id → Set("a-b") of rows about to be replaced
    filesById.forEach((f, parentId) => {
      if (f?.cut?.state !== 'updating') return;
      const recent = now - (ruledAt.get(parentId) || 0) < RECENT_RULING_MS;
      if (!active.has(parentId) && !recent) return;
      out.set(parentId, new Set((f.cut.stale || []).map(([a, b]) => `${a}-${b}`)));
    });
    return out;
  }, [jobs, filesById, ruledAt]);

  // every row by id and each cut file's child count, from the UNFILTERED list (a filter or the unit may hide them)
  const { docsById, realChildCount } = useMemo(() => {
    const byId = new Map(); const counts = new Map();
    for (const d of allDocs || docs || []) {
      byId.set(d.id, d);
      if (isCutChild(d)) counts.set(d.container_id, (counts.get(d.container_id) || 0) + 1);
    }
    return { docsById: byId, realChildCount: counts };
  }, [allDocs, docs]);

  const entries = useMemo(() => {
    const out = [];
    for (const doc of docs || []) {
      if (isCutChild(doc)) {
        // a document cut out of a file: one card, with its parent for the provenance line (Documents unit)
        out.push({ kind: 'file', doc, file: null, parent: docsById.get(doc.container_id) || null, key: `f:${doc.id}` });
        continue;
      }
      if (doc.is_compilation_container) {
        // a cut file: a card with its "N documents inside" badge (Files unit; Documents unit with sources shown)
        out.push({ kind: 'file', doc, file: { is_compilation: true, segment_count: realChildCount.get(doc.id) || 0 }, key: `f:${doc.id}` });
        continue;
      }
      const file = filesById.get(doc.id);
      if (file?.is_compilation && isDocsUnit) {
        // detected but not cut yet: its documents are cards of their own; the file itself only with sources shown
        if (showSourceFiles) out.push({ kind: 'file', doc, file, key: `f:${doc.id}` });
        for (const item of itemsByParent.get(doc.id) || []) out.push({ kind: 'child', doc, item, key: `c:${item.id}` });
      } else {
        out.push({ kind: 'file', doc, file: file || null, key: `f:${doc.id}` });
      }
    }
    return out;
  }, [docs, docsById, realChildCount, filesById, itemsByParent, showSourceFiles, isDocsUnit]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const k = groupKeyFor(e, groupBy);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    const keys = orderGroups([...m.keys()], groupBy);
    return keys.map((k) => ({ key: k, entries: m.get(k).sort(readingOrder) }));
  }, [entries, groupBy]);

  // the card list's content width and its distance from the top of the page (the window
  // scrolls, so the virtualizer needs to know where the list starts)
  const listRef = useRef(null);
  const [listBox, setListBox] = useState({ width: 0, top: 0 });
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    const read = () => {
      const r = el.getBoundingClientRect();
      const width = Math.floor(r.width);
      const top = Math.round(r.top + window.scrollY);
      setListBox((cur) => (cur.width === width && Math.abs(cur.top - top) < 2 ? cur : { width, top }));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    ro.observe(document.body);              // a banner above the list opening or closing moves its top
    return () => ro.disconnect();
  }, []);
  const cols = density === 'rows' || !listBox.width
    ? (density === 'rows' ? 1 : 4)
    : Math.max(1, Math.floor((listBox.width - GRID_PAD_X + CARD_GAP) / (CARD_MIN + CARD_GAP)));

  // groups → lines: a header, then the group's cards `cols` to a row
  const lines = useMemo(() => {
    const out = [];
    for (const g of groups) {
      out.push({ kind: 'head', key: `h:${g.key}`, group: g });
      for (let i = 0; i < g.entries.length; i += cols) {
        out.push({ kind: 'cards', key: `r:${g.key}:${g.entries[i].key}`, group: g, entries: g.entries.slice(i, i + cols), first: i === 0 });
      }
    }
    return out;
  }, [groups, cols]);
  const estimateSize = useCallback((i) => (lines[i]?.kind === 'head' ? LINE_EST.head : (density === 'rows' ? LINE_EST.rows : LINE_EST.cards)), [lines, density]);
  const getItemKey = useCallback((i) => lines[i]?.key ?? i, [lines]);
  const virtualizer = useWindowVirtualizer({
    count: lines.length,
    estimateSize,
    getItemKey,
    overscan: density === 'rows' ? OVERSCAN.rows : OVERSCAN.cards,
    scrollMargin: listBox.top,
  });
  const vLines = virtualizer.getVirtualItems();
  const padTop = vLines.length ? Math.max(0, vLines[0].start - listBox.top) : 0;
  const padBottom = vLines.length ? Math.max(0, virtualizer.getTotalSize() - (vLines[vLines.length - 1].end - listBox.top)) : 0;

  const kindCounts = useMemo(() => {
    const k = { file: 0, realchild: 0, child: 0, container: 0 };
    for (const e of entries) {
      if (e.kind === 'child') k.child += 1;
      else if (e.file?.is_compilation) k.container += 1;
      else if (isCutChild(e.doc)) k.realchild += 1;
      else k.file += 1;
    }
    return k;
  }, [entries]);
  const groupIndex = useMemo(() => JSON.stringify(groups.map((g) => [g.key, g.entries.length])), [groups]);

  const counts = useMemo(() => {
    let children = 0; let unsure = 0; let plain = 0; let containers = 0; let inside = 0;
    const parents = new Set();
    for (const e of entries) {
      if (e.kind === 'child') { children += 1; parents.add(e.doc.id); if (e.item.unsure) unsure += 1; }
      else if (isCutChild(e.doc)) { children += 1; parents.add(e.doc.container_id); }
      else if (e.file?.is_compilation) { containers += 1; inside += e.file.segment_count || 0; }
      else plain += 1;
    }
    return { documents: plain + children, children, parents: parents.size, unsure, files: plain + containers, containers, inside };
  }, [entries]);

  const open = useCallback((docId, page = null) => { onShowViewer?.(docId, null, page); }, [onShowViewer]);
  const select = useCallback((docId) => { setDetailId((cur) => (cur === docId ? null : docId)); }, []);
  const toggleCheck = useCallback((docId) => { dispatch({ type: 'SELECT_DOC', id: docId }); }, [dispatch]);
  const dupFilter = useCallback((hash) => { dispatch({ type: 'SET_DUPLICATE_HASH_FILTER', hash }); }, [dispatch]);
  const rule = useCallback(async (parentId, action, page, extra = {}, item = null) => {
    setBusy(true); setRuleError(null);
    try {
      await api.post(`/documents/${parentId}/segments/decisions`, { action, page, ...extra });
      setRuledAt((m) => new Map(m).set(parentId, Date.now()));
      announceSegmentsChanged(parentId, 'gallery');   // panel + viewer follow
      undoRef.current = parentId;
      showToast(rulingLabel(action, page, extra, item));
      await refresh();
    } catch (e) {
      setRuleError(e?.message || `Could not ${action}`);
    } finally {
      setBusy(false);
    }
  }, [refresh, showToast]);
  const undo = useCallback(async () => {
    const parentId = undoRef.current;
    if (!parentId) return;
    setBusy(true); setRuleError(null);
    try {
      await api.post(`/documents/${parentId}/segments/decisions/undo`, {});
      setRuledAt((m) => new Map(m).set(parentId, Date.now()));
      announceSegmentsChanged(parentId, 'gallery');
      clearToast();
      await refresh();
    } catch (e) {
      setRuleError(e?.message || 'Could not undo');
    } finally {
      setBusy(false);
    }
  }, [refresh, clearToast]);

  // the detail pane follows the row it shows; it closes when that row is gone (a re-cut replaced it) or on Escape
  const detailDoc = detailId ? docsById.get(detailId) || null : null;
  useEffect(() => { if (detailId && !docsById.has(detailId)) setDetailId(null); }, [detailId, docsById]);
  useEffect(() => {
    if (!detailId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDetailId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailId]);
  const detailParent = detailDoc && isCutChild(detailDoc) ? docsById.get(detailDoc.container_id) : null;

  // Opening or closing the pane (or resizing the window) changes how many cards fit a row, so
  // every row below the top re-flows: bring the card the reader was on back to the middle.
  const anchorRef = useRef({ id: null, closedAt: 0 });      // the selected card; kept for a moment after the pane closes
  if (detailId) anchorRef.current = { id: detailId, closedAt: 0 };
  useEffect(() => { if (!detailId && anchorRef.current.id) anchorRef.current.closedAt = Date.now(); }, [detailId]);
  const colsRef = useRef(cols);
  useLayoutEffect(() => {
    if (colsRef.current === cols) return;
    colsRef.current = cols;
    const { id, closedAt } = anchorRef.current;
    if (!id || !listBox.width || (closedAt && Date.now() - closedAt > 1500)) return;
    const idx = lines.findIndex((l) => l.kind === 'cards' && l.entries.some((e) => (e.kind === 'child' ? e.item.parent_id : e.doc.id) === id));
    if (idx >= 0) virtualizer.scrollToIndex(idx, { align: 'center' });
  }, [cols, lines, listBox.width, virtualizer]);

  const nothingDetected = isDocsUnit && data && data.compilation_count === 0 && counts.children === 0;

  return (
    <div className="inv-gallery-wrap">
      <div ref={galleryRef} className={`inv-gallery inv-gallery--${density}${detailDoc ? ' inv-gallery--with-detail' : ''}`} data-testid="document-gallery" data-unit={unit} data-density={density}
        data-cards={entries.length} data-cards-file={kindCounts.file} data-cards-realchild={kindCounts.realchild}
        data-cards-child={kindCounts.child} data-cards-container={kindCounts.container} data-groups={groupIndex}>
        <div className="inv-gal-bar">
          <div className="inv-gal-summary" data-testid="gallery-summary">
            {loading && !data ? 'Loading detected documents…' : isDocsUnit ? (
              <>
                <strong>{counts.documents}</strong> {counts.documents === 1 ? 'document' : 'documents'}
                {counts.children > 0 && <> · <strong>{counts.children}</strong> inside {counts.parents} {counts.parents === 1 ? 'file' : 'files'}</>}
                {counts.unsure > 0 && <> · <span className="inv-gal-proposed-count" title="Boundaries the detector was not sure about are marked ?">not sure about {counts.unsure}</span></>}
                {updatingRanges.size > 0 && <> · <span className="inv-gal-updating" data-testid="gallery-updating" title="A correction is being applied: the affected documents are cut again from their source file">updating {updatingRanges.size} {updatingRanges.size === 1 ? 'file' : 'files'}…</span></>}
              </>
            ) : (
              <>
                <strong>{counts.files}</strong> {counts.files === 1 ? 'file' : 'files'}
                {counts.containers > 0 && <> · <strong>{counts.containers}</strong> {counts.containers === 1 ? 'holds' : 'hold'} {counts.inside} documents</>}
              </>
            )}
            {error && <span className="inv-gal-error"> · {error}</span>}
            {ruleError && <span className="inv-gal-error"> · {ruleError}</span>}
          </div>
          <div className="inv-gal-controls">
            <label className="inv-gal-groupby">
              <span>Group by</span>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="gallery-groupby">
                {groupOptions.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </label>
            <div className="inv-view-toggle inv-gal-density" role="group" aria-label="Density">
              <button type="button" className={`inv-view-btn ${density === 'cards' ? 'inv-view-btn--active' : ''}`} title="Cards" onClick={() => setDensity('cards')}>▦</button>
              <button type="button" className={`inv-view-btn ${density === 'rows' ? 'inv-view-btn--active' : ''}`} title="Rows" onClick={() => setDensity('rows')}>☰</button>
            </div>
          </div>
        </div>

        {nothingDetected && (
          <div className="inv-gal-hint">
            No file has been split into documents yet, so every document here is a file. Long PDFs are checked for
            documents inside them automatically; you can also expand a file's row and use <em>Find documents</em>.
          </div>
        )}

        {groups.length === 0 && !loading && (
          <div className="inv-grid-empty">No {isDocsUnit ? 'documents' : 'files'} match the current filters.</div>
        )}

        <UndoToast toast={toast} busy={busy} onUndo={undo} onClose={clearToast} />

        <div ref={listRef} className="inv-gal-vlist" data-testid="gallery-list" style={{ paddingTop: padTop, paddingBottom: padBottom }}>
          {vLines.map((v) => {
            const line = lines[v.index];
            if (!line) return null;
            if (line.kind === 'head') {
              return (
                <div key={v.key} ref={virtualizer.measureElement} data-index={v.index} className="inv-gal-line inv-gal-line--head" data-gal-group={line.group.key}>
                  <header className="inv-gal-group-head">
                    <span className="inv-gal-group-title">{line.group.key}</span>
                    <span className="inv-gal-group-count">{line.group.entries.length}</span>
                  </header>
                </div>
              );
            }
            return (
              <div key={v.key} ref={virtualizer.measureElement} data-index={v.index} className={`inv-gal-line inv-gal-line--cards${line.first ? ' inv-gal-line--first' : ''}`} data-gal-group={line.group.key}>
                <div className="inv-gal-grid" style={density === 'rows' ? undefined : { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                  {line.entries.map((e) => (e.kind === 'child'
                    ? <MemoChild key={e.key} entry={e} fitTitle={fitTitle} onSelect={select} onOpen={open} onRule={rule} busy={busy} />
                    : <MemoFile key={e.key} entry={e} fitTitle={fitTitle} selected={detailId === e.doc.id} checked={checkedSet.has(e.doc.id)} onSelect={select} onToggleCheck={toggleCheck} onDupFilter={dupFilter} onOpen={open} onRule={rule} busy={busy}
                        dupCount={e.doc.file_hash_sha256 ? (hashCountMap[e.doc.file_hash_sha256] || 0) : 0}
                        updating={isCutChild(e.doc) && !!updatingRanges.get(e.doc.container_id)?.has(`${e.doc.page_start}-${e.doc.page_end}`)} />))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {detailDoc && (
        <aside className="inv-gal-detail" data-testid="gallery-detail" data-gal-detail-doc={detailDoc.id} aria-label="Document details">
          <header className="inv-gal-detail-head">
            <div className="inv-gal-detail-title" title={detailDoc.filename}>
              {docTitle(detailDoc, detailParent?.filename)}
              {isCutChild(detailDoc) && <div className="inv-gal-detail-sub">{pagesRange(detailDoc.page_start, detailDoc.page_end)} · in {detailParent?.filename || 'its source file'}</div>}
            </div>
            <button type="button" className="btn btn-sm inv-gal-detail-close" onClick={() => setDetailId(null)} aria-label="Close details" title="Close (Esc)">✕</button>
          </header>
          <DocumentDetailPanel
            key={detailDoc.id}
            doc={detailDoc}
            caseId={caseId}
            folderPaths={folderPaths}
            onDocChange={onDocChange}
            onShowDetail={onShowDetail}
            onShowViewer={onShowViewer}
          />
        </aside>
      )}
    </div>
  );
}
